import classNames from 'classnames';
import React, { forwardRef, useEffect, useId, useState } from 'react';
import InlineError from '../InlineError';
import Select from '../Select';
import Tooltip from '../Tooltip';
import './DurationSelect.scss';

interface DurationSelectProps {
  maxYears?: number;
  maxMonths?: number;
  offset?: number;
  onChange?: ({ years, months }: { years: number; months: number }) => void;
  label: React.ReactNode | React.ReactNode[];
  name: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | boolean;
  defaultValue?: string | number;
  helpText?: React.ReactNode | React.ReactNode[];
  hideLabel?: boolean;
  tooltip?: React.ReactNode | React.ReactNode[];
  tooltipAriaLabel?: string;
  pii?: boolean;
  value?: {
    years: number;
    months: number;
  };
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
}

const DurationSelect = forwardRef<HTMLSelectElement, DurationSelectProps>(
  (
    {
      maxYears = 40,
      maxMonths = 11,
      offset = 0,
      name,
      pii,
      error,
      hideLabel,
      label,
      required,
      tooltip,
      tooltipAriaLabel,
      value,
      onChange
    },
    ref
  ) => {
    const id = useId();

    const [years, setYears] = useState<number>(value?.years);
    const [months, setMonths] = useState<number>(value?.months);

    const maxYearsWithOffset = maxYears + offset;

    const onSelectChange = (key: 'years' | 'months', value: number) => {
      const yearsValue = key === 'years' ? value : years;
      const monthsValue = key === 'months' ? value : yearsValue === maxYearsWithOffset ? 0 : months;

      if (yearsValue !== undefined && monthsValue !== undefined) {
        onChange?.({
          years: yearsValue,
          months: monthsValue
        });
      }
    };

    const yearOptions = Array.from({ length: maxYears + 1 }, (_, index) => ({
      label: `${index + offset}`.padStart(2, '0'),
      value: index + offset
    }));

    const monthOptions = Array.from({ length: maxMonths + 1 }, (_, index) => ({
      label: `${index}`.padStart(2, '0'),
      value: index
    }));

    const isMonthDisabled = years === maxYearsWithOffset;

    const removeLeadingZero = (str: string) => {
      return str.replace(/^0/, '');
    };

    /**
     * When value is changed we need to update the state to reflect that
     */
    useEffect(() => {
      if (value) {
        setYears(value?.years);
        setMonths(value?.months);
      }
    }, [value]);

    return (
      <fieldset
        className={classNames('mss-form-control mss-duration-input', {
          'ctHidden pii-data': pii
        })}
        aria-describedby={error ? `error-${id}` : undefined}
        aria-labelledby={`duration-input-label-${id}`}
      >
        <div
          id={`duration-input-label-${id}`}
          className={classNames('mss-form-control-label', {
            'sr-only': hideLabel
          })}
        >
          {label}
          {required && (
            <span className="mss-form-control-required" aria-hidden>
              *
            </span>
          )}
          {tooltip && (
            <Tooltip content={tooltip} buttonProps={{ 'aria-label': tooltipAriaLabel }} />
          )}
        </div>
        <div>
          <Select
            label="Years"
            suggestions={yearOptions}
            name={`${name}-years`}
            placeholder="YY"
            value={years}
            ref={ref}
            onChange={(value) => {
              setYears(value);

              if (value === maxYearsWithOffset) {
                setMonths(0);
              }

              onSelectChange('years', value);
            }}
            suggestionRenderer={(label) => (
              <span aria-label={`${removeLeadingZero(label)} years`}>{label}</span>
            )}
            hideLabel
          />
          <span>Years</span>
          <Select
            label="Months"
            suggestions={monthOptions}
            name={`${name}-months`}
            value={months}
            onChange={(value) => {
              setMonths(value);
              onSelectChange('months', value);
            }}
            disabled={isMonthDisabled}
            placeholder="MM"
            suggestionRenderer={(label) => (
              <span aria-label={`${removeLeadingZero(label)} months`}>{label}</span>
            )}
            hideLabel
          />
          <span>Months</span>
        </div>
        <span role="alert">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {((error as any) instanceof String || typeof error === 'string') && (
            <InlineError error={error as string} id={id} />
          )}
        </span>
      </fieldset>
    );
  }
);

export default DurationSelect;
